import React, {
	FC, KeyboardEvent, useState, useRef, useEffect, useMemo,
} from 'react';
import { motion } from 'framer-motion';
import classNames from 'classnames';
import {
	alephToast,
	Button,
	ButtonColor,
	IColumnEditor,
	NotificationToast,
	NotificationType,
	Tooltip,
	TrashCanIcon,
} from '@aleph/shared-components';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
	SpotifyMediaListDocument,
	MediaType,
	DeleteAssetsDocument,
	SpotifyMediaListQueryItems,
} from 'wb-spotify/graphql/generated';
import Music from 'wb-spotify/assets/music-note.png';
import Video from 'wb-spotify/assets/video.png';
import Fallback from 'wb-spotify/assets/fallback.png';
import Player from 'wb-spotify/app/routes/superGrid/columnEditors/media/player';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';

// TODO for now BE doesn't support media deletion when it's done
//  -> replace DeleteAssetsDocument with it and remove this constant
const DELETE_HIDDEN = true;

// See packages/wb-spotify/src/app/routes/superGrid/api/fragments/ad.graphql for the names of the fields in supergrid
type SupergridAsset = {
	mediaThumbnail: string;
	mediaUrl: string;
	name: string;
	id: string;
};

interface IProps extends IColumnEditor<SupergridAsset> {
	mediaTypes: MediaType[];
	type: 'asset' | 'companion' | 'logo' | 'canvas';
}

export const fallBackCover = (assetType: MediaType) => {
	switch (assetType) {
		case MediaType.Video:
			return Video;
		case MediaType.Audio:
			return Music;
		case MediaType.Image:
		default:
			return Fallback;
	}
};

type SpotifyMedia = SpotifyMediaListQueryItems;

const SpotifyMediaColumnEditor: FC<IProps> = (params) => {
	const [allMediaItems, setAllMediaItems] = useState<SpotifyMedia[]>([]);
	const { t } = useTranslation();
	const {
		value, setValue, mediaTypes, type,
	} = params;

	const parentRef = useRef<HTMLDivElement>(null);
	const infiniteScrollCursor = useRef<string | undefined>(undefined);
	const [playerState, setPlayerState] = useState({
		isOpen: false,
		url:    '',
		type:   'video' as 'video' | 'audio',
		name:   '',
	});

	const [getMedia, { loading }] = useLazyQuery(SpotifyMediaListDocument, {
		variables: {
			mediaTypes,
		},
		fetchPolicy:                 'network-only',
		notifyOnNetworkStatusChange: true,
	});

	const isLoadingMore = loading && allMediaItems.length > 0;

	// Group items into rows of 3 for grid layout
	const rows = useMemo(() => {
		const rowArray: SpotifyMedia[][] = [];

		for (let i = 0; i < allMediaItems.length; i += 3) {
			rowArray.push(allMediaItems.slice(i, i + 3));
		}

		return rowArray;
	}, [allMediaItems]);

	const rowVirtualizer = useVirtualizer({
		count:            rows.length + (infiniteScrollCursor.current ? 1 : 0),
		getScrollElement: () => parentRef.current,
		estimateSize:     () => 95, // Approximate size of each row (item height + gap)
		overscan:         10,
	});

	const [deleteAsset, { loading: deleteLoading }] = useMutation(DeleteAssetsDocument, {
		errorPolicy:         'all',
		refetchQueries:      [SpotifyMediaListDocument],
		awaitRefetchQueries: true,
	});

	const handleDeleteMedia = async (id: string, hash: string) => {
		// TODO when BE supports media deletion change it
		try {
			await console.info(deleteAsset, id, hash);
		} catch (e) {
			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={t('re: general_err.title', 'Oh no! Something went wrong.')}
				/>,
			);
		}
	};

	const handlePlayMedia = (media: SpotifyMedia) => {
		setPlayerState({
			isOpen: true,
			url:    media?.mediaUrl ?? '',
			type:   media?.mediaType === MediaType.Video ? 'video' : 'audio',
			name:   media?.mediaOriginalFileName ?? '',
		});
	};

	const clearPlayerState = () => {
		setPlayerState({
			isOpen: false,
			url:    '',
			type:   'video',
			name:   '',
		});
	};

	const selectMedia = (asset?: SpotifyMedia) => () => {
		if (asset) {
			setValue({
				name:           asset?.mediaOriginalFileName ?? '',
				mediaThumbnail: asset?.mediaThumbnail ?? '',
				mediaUrl:       asset?.mediaUrl ?? '',
				id:             asset?.mediaId ?? '',
				// @ts-ignore TODO fix type
				mediaType:      asset?.mediaType ?? MediaType.Image,
				hash:           `${type}_${asset?.mediaId}`,
			});
		}
	};

	const onKeyDown = (e: KeyboardEvent) => {
		switch (e.key) {
			case 'Enter':
				params.api.stopEditing();

				break;
			case 'Escape':
				params.api.stopEditing(true);

				break;
			case 'ArrowLeft':
				{
					const currSelectedIndex = allMediaItems?.findIndex(
						(media) => media?.mediaUrl === value?.mediaUrl,
					) ?? 0;
					const newItem = allMediaItems?.[currSelectedIndex > 0 ? currSelectedIndex - 1 : 0];

					if (newItem) {
						selectMedia(newItem)();
					}
				}

				break;
			case 'ArrowRight':
				{
					const currSelectedIndex = allMediaItems?.findIndex(
						(media) => media?.mediaUrl === value?.mediaUrl,
					) ?? 0;
					const newItem = allMediaItems?.[currSelectedIndex + 1];

					if (newItem) {
						selectMedia(newItem)();
					}
				}

				break;
		}
	};

	const virtualItems = rowVirtualizer.getVirtualItems();
	const totalSize = rowVirtualizer.getTotalSize();

	useEffect(() => {
		async function fetchMediaItems() {
			const lastItem = virtualItems[virtualItems.length - 1];
			const allItems = allMediaItems ?? [];

			if (!lastItem && !allItems.length) {
				// Initial fetch
				const { data: initialData } = await getMedia({
					variables: {
						mediaTypes,
					},
				});

				setAllMediaItems(initialData?.assets?.items ?? []);

				if (initialData?.assets?.cursor) {
					infiniteScrollCursor.current = initialData?.assets.cursor;
				}

				return;
			}

			// Each row contains 3 items, so we need to calculate the index of the last item in the last row
			const lastRowIndex = Math.floor(allItems.length / 3);

			if (
				(lastItem?.index >= lastRowIndex && infiniteScrollCursor.current && !isLoadingMore)
			) {
				const { data: fetchedData } = await getMedia({
					variables: {
						mediaTypes,
						cursor: infiniteScrollCursor.current,
					},
				});

				setAllMediaItems(curr => ([...curr, ...(fetchedData?.assets?.items ?? [])]));

				infiniteScrollCursor.current = fetchedData?.assets?.cursor ?? undefined;
			}
		}

		fetchMediaItems();
	}, [virtualItems, mediaTypes, allMediaItems]);

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			className="pl-[8px] py-[8px] h-full"
			onKeyDown={onKeyDown}
		>
			<Player {...playerState} />

			<div
				ref={parentRef}
				className="overflow-auto h-full"
			>
				<div
					style={{
						height:   `${totalSize}px`,
						width:    '100%',
						position: 'relative',
					}}
				>
					{virtualItems.map((virtualRow) => {
						const rowItems = rows[virtualRow.index] ?? [];

						return (
							<div
								key={virtualRow.index}
								ref={rowVirtualizer.measureElement}
								data-index={virtualRow.index}
								style={{
									position:  'absolute',
									top:       0,
									left:      0,
									width:     '100%',
									height:    `${virtualRow.size}px`,
									transform: `translateY(${virtualRow.start}px)`,
								}}
								className="grid grid-cols-3 gap-[8px]"
							>
								{rowItems.map((media, colIndex) => {
									const handleImageError = (e) => {
										e.target.onerror = null;
										// @ts-ignore
										e.target.src = fallBackCover(media.mediaType);
									};

									const onClick = () => {
										if (
											media?.mediaType === MediaType.Video
											|| media?.mediaType === MediaType.Audio
										) {
											handlePlayMedia(media);
										} else {
											clearPlayerState();
										}
										selectMedia(media)();
									};

									const itemIndex = virtualRow.index * 3 + colIndex;

									return (
										<Tooltip
											key={media.mediaId ?? itemIndex}
											content={media?.mediaOriginalFileName ?? ''}
											size="medium"
											className="z-modal"
										>
											<button
												autoFocus={itemIndex === 0 || value?.mediaUrl === media?.mediaUrl}
												className={classNames(
													'group flex outline-0 items-center bg-[#e9e9e9] '
														+ 'border-[2px] hover:border-wise-blue cursor-pointer relative',
													{
														'border-wise-blue': value?.mediaUrl === media?.mediaUrl,
														'border-white':     value?.mediaUrl !== media?.mediaUrl,
													},
												)}
												onClick={onClick}
												onDoubleClick={() => params.api.stopEditing()}
												disabled={deleteLoading}
											>
												<img
													alt={media?.mediaOriginalFileName ?? ''}
													onError={handleImageError}
													className="h-full w-full aspect-square object-cover"
													src={media?.mediaUrl ?? ''}
												/>
												{!DELETE_HIDDEN && (
													<Button
														onClick={async (e) => {
															e.stopPropagation();
															// TODO when BE supports media deletion change it,
															// show confirmation dialog before deletion
															await handleDeleteMedia(
																media?.mediaId ?? '',
																media?.mediaId ?? '',
															);
														}}
														leftNode={<TrashCanIcon />}
														appearance="ghost"
														disabled={deleteLoading}
														loading={deleteLoading}
														color={ButtonColor.RED}
														className={
															'absolute top-0 right-0 opacity-0 '
															+ 'group-hover:opacity-100 transition-opacity duration-200'
														}
														data-testid="asset-delete-button"
													/>
												)}
											</button>
										</Tooltip>
									);
								})}
							</div>
						);
					})}
				</div>
			</div>
		</motion.div>
	);
};

export default SpotifyMediaColumnEditor;
